import React, { useEffect, useState } from 'react';
import styles from './QuoteTable.module.scss';
import { Grid, Typography } from '@mui/material';
import { Box } from '@mui/system';
import { useTranslation } from 'react-i18next';
import Button from "@mui/material/Button";
import { Table, TableBody, TableCell, TableContainer, TableHead, TableRow, Paper, Checkbox } from '@mui/material';
import { HTTPMethod, callApi as QuoteTableRequest } from '../../../services/HttpService/HttpService';
import { OrdersComponentApi } from '../../../constants/ApiConstants';
import QuoteDenyAlert from '../QuoteDenyAlert/QuoteDenyAlert';
import QuoteApproveAlert from '../QuoteApproveAlert/QuoteApproveAlert';
import { errorToast, successToast } from '../../../components/ToastMessage/ToastMessage';
import { QuoteCustomFields, quoteStatus } from '../../../common/enums';
import ItemLabel from '../../../components/ItemLabel/ItemLabel';

const QuoteTable = ({ paceQuoteItemList, paceQuoteAccessModel, orderCustomFields, orderStatus, handleOrdersDetails, commentForClient, commentsForProvider, commentsForStateServ }) => {
  const { t } = useTranslation();
  const [isOpenDenyAlert, setIsOpenDenyAlert] = useState(false);
  const [isOpenApproveAlert, setIsOpenApproveAlert] = useState(false);
  const [quoteItemList, setQuoteItemList] = useState(paceQuoteItemList);
  const [allChecked, setAllChecked] = useState(true);
  const [deniedItems, setDeniedItems] = useState(paceQuoteItemList?.filter(item => !item.enabled).map(item => item?.pace_quote_item_id));
  const [dxCode, setDxCode] = useState(null);
  const [authCode, setAuthCode] = useState(null);
  const [actionTaken, setActionTaken] = useState(false);
  const [totalPrice, setTotalPrice] = useState(0);
  const [isSubmitted, setIsSubmitted] = useState(false);
  const isOrderStatusInClientReview = (orderStatus == quoteStatus.clientReview);

  const handleDenyAlert = async () => {
    await handleDenyApproveAlert("", "", true);
    const denied = quoteItemList?.map((item) => item?.pace_quote_item_id);
    setQuoteItemList(
      quoteItemList?.map(item => ({
        ...item,
        enabled: false
      }))
    );
    setDeniedItems(denied);
    setIsOpenDenyAlert(false);
    setActionTaken(true);
  };

  const handleApproveAlert = async (dxCodeValue, authCodeValue) => {
    await handleDenyApproveAlert(dxCodeValue, authCodeValue);
    const denied = quoteItemList?.filter(item => !item.enabled)?.map(item => item?.pace_quote_item_id);
    setDeniedItems(denied);
    setIsOpenApproveAlert(false);
    setActionTaken(true);
  };

  const handleSelectAll = (event) => {
    const checked = event?.target?.checked;
    const updatedData = quoteItemList?.map(item => ({
      ...item,
      enabled: checked
    }));
    setQuoteItemList(updatedData);
    setAllChecked(checked);
  };

  const handleApproveToggle = (index) => {
    const updatedData = [...quoteItemList];
    updatedData[index].enabled = !updatedData[index].enabled;
    setQuoteItemList(updatedData);
  };

  const handleDenyApproveAlert = async (dxCodeValue = "", authCodeValue = "", denyAllItems = false) => {
    setIsSubmitted(true);
    const approvedItems = denyAllItems
      ? []
      : quoteItemList?.filter(item => item?.enabled)?.map(item => ({
        pace_quote_item_id: item?.pace_quote_item_id,
      }));
    const orderId = location.pathname.split("/").pop();
    const quoteRequestBody = {
      items: approvedItems,
      comments_for_state_serv: commentsForStateServ ?? "",
      order_id: orderId,
      prior_auth: authCodeValue,
      dx_code: dxCodeValue,
      comments_for_client: commentForClient ?? "",
      comments_for_provider: commentsForProvider ?? ""
    }
    const response = await QuoteTableRequest(
      HTTPMethod.Post,
      OrdersComponentApi.denyApproveQuoteItems, quoteRequestBody
    );
    if (response && response?.status_code == 200 && response?.data) {
      if (approvedItems?.length > 0) {
        successToast(t('Quote.successToastApprovedSuccessfully'))
      } else {
        errorToast(t('Quote.errorToastDeniedSuccessfully'))
      }
      await handleOrdersDetails();
    }
    setIsSubmitted(false);
  };

  useEffect(() => {
    const total = quoteItemList?.filter(item => item?.enabled)?.reduce((sum, item) => sum + item?.quantity * item?.client_price, 0);
    setTotalPrice(total);
  }, [quoteItemList]);

  useEffect(() => {
    if (orderCustomFields?.length) {
      const dx = orderCustomFields?.find((item) => item?.column === QuoteCustomFields.dxCode);
      const auth = orderCustomFields?.find((item) => item?.column === QuoteCustomFields.authCode);
      if (dx) {
        setDxCode(dx);
      }
      if (auth) {
        setAuthCode(auth);
      }
    }
  }, [orderCustomFields?.length]);

  return (
    <div data-testid="QuoteTable">
      {quoteItemList?.length > 0 && <Grid className='marginTop10px'>
        <Grid>
            <TableContainer className={styles.borderedScrollable}>
              <Table size="size" aria-label="simple table">
                <TableHead>
                  <TableRow className={styles.lightGrayBackground}>
                    <TableCell className={`${styles.boldPaddedText} ${styles.fixedWidth}`} align="left">{t("Quote.hcpcItem")}</TableCell>
                    <TableCell className={styles.boldPaddedText} align="left">{t("PatientsDetails.insightDescription")}</TableCell>
                    <TableCell className={styles.boldPaddedText} align="right">{t("NewOrder.quantity")}</TableCell>
                    <TableCell className={styles.boldPaddedText} align="right">{t("NewOrder.price")}</TableCell>
                    <TableCell className={styles.boldPaddedText} align="right">{t("Quote.total")}</TableCell>
                    <TableCell className={`${styles.boldPaddedText} ${styles.fixedWidthCheckBox}`} align="center">
                      <Checkbox
                        color="primary"
                        data-testid="headerCheckBox"
                        checked={allChecked}
                        disabled={actionTaken || !isOrderStatusInClientReview}
                        onChange={handleSelectAll}
                      />
                      {t("Quote.approveItems")}
                    </TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {quoteItemList?.map((row, index) => {
                    const strikeStyle = deniedItems?.includes(row?.pace_quote_item_id) ? { textDecoration: 'line-through' } : {};
                    return (
                      <TableRow
                        key={index}
                        sx={{ backgroundColor: index % 2 === 0 ? 'var(--bg-white)' : 'var(--color-whitesmoke-100)' }}
                      >
                        <TableCell style={strikeStyle} className={styles.boldPaddedText} align="left">{row.serial}</TableCell>
                        <TableCell style={strikeStyle} className={styles.boldPaddedText} align="left">{row.description}</TableCell>
                        <TableCell style={strikeStyle} className={styles.boldPaddedText} align="right">{row.quantity}</TableCell>
                        <TableCell style={strikeStyle} className={styles.boldPaddedText} align="right">${row?.client_price?.toFixed(2) ?? 0}</TableCell>
                        <TableCell style={strikeStyle} className={styles.boldPaddedText} align="right">${(row?.client_price * row?.quantity)?.toFixed(2) ?? 0}</TableCell>
                        <TableCell className={styles.boldPaddedTextRow} align="center">
                          <Checkbox
                            color="primary"
                            data-testid="checkbox"
                            checked={row?.enabled}
                            disabled={actionTaken || !isOrderStatusInClientReview}
                            onClick={() => handleApproveToggle(index)}
                          />
                        </TableCell>
                      </TableRow>
                    );
                  })}
                  <TableRow className={styles.grayBackground}>
                    <TableCell colSpan={6} align="right">
                      <Box className={`${styles.flexEndWrap} ${styles.boldPaddedText}`}>
                        <Typography variant="body1" fontWeight="bold">{t("reports.total")}</Typography>
                        <Typography variant="body1" fontWeight="bold">${totalPrice?.toFixed(2) ?? 0}</Typography>
                      </Box>
                    </TableCell>
                  </TableRow>
                </TableBody>
              </Table>
            </TableContainer>
        </Grid>
        <Grid item className='marginTop10px'>
          <ItemLabel label={t('Quote.commentForClient')} text={commentForClient} />
        </Grid> 
        <Grid item xs={12} >
          {(!actionTaken && isOrderStatusInClientReview) && <Box className={styles.responsiveButtonContainer}>
            <Button
              className='marginTop10px marginBottom10px'
              color="error"
              size="small"
              disabled={!paceQuoteAccessModel?.is_pace_quote_received_by_approve && !paceQuoteAccessModel?.is_pace_quote_received_by_review && !paceQuoteAccessModel?.is_pace_quote_cancelled && !paceQuoteAccessModel?.can_approve_order}
              onClick={() => {
                setIsOpenDenyAlert(true);
              }}
            >
              {t("Quote.denyAllItems")}
            </Button>
            <Button
              className='marginTop10px marginBottom10px'
              color="primary"
              size="small"
              disabled={!quoteItemList?.some(item => item?.enabled) || (!paceQuoteAccessModel?.is_pace_quote_received_by_approve && !paceQuoteAccessModel?.is_pace_quote_received_by_review && !paceQuoteAccessModel?.is_pace_quote_cancelled && !paceQuoteAccessModel?.can_approve_order) }
              onClick={() => {
                setIsOpenApproveAlert(true);
              }}
            >
              {t("Quote.approveSelectedItems")}
            </Button>
          </Box>}
        </Grid>
      </Grid>}
      {isOpenDenyAlert && <QuoteDenyAlert isOpen={isOpenDenyAlert} handleCancel={() => { setIsOpenDenyAlert(false); }}
        handleSubmit={handleDenyAlert} deniedItems={quoteItemList} isSubmitted={isSubmitted} />}
      {isOpenApproveAlert && <QuoteApproveAlert isOpen={isOpenApproveAlert} handleCancel={() => { setIsOpenApproveAlert(false); }}
        handleApprovalSubmit={handleApproveAlert} quoteItemList={quoteItemList} dxCode={dxCode} authCode={authCode} 
        isSubmitted={isSubmitted}/>}
    </div>
  )
};

export default QuoteTable;